import {
    Collapse, 
    Grid, 
    IconButton,
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TablePagination, 
    TableRow, 
    Tooltip,
    Table,
    Paper
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { AppState } from "../reducers";
import { LoadableProfileData } from "../reducers/userProfiles";
import { GridCard } from "../StyledComponents";
import { GameBehavior } from "../types/GameBehaviorLog";
import { GameLog } from "../types/GameLog";
// import MahjongSummaryTable from "./MahjongSummaryTable";
import { MahjongGameRecord } from "./MahjongGameRecord";
import { MahjongBehaviorAverageData } from "../api";

interface MahjongHistoryTableProps {
    mahjongData: GameLog[] | undefined,
    behaviorData: GameBehavior[],
    mahjongBehaviorAverage: MahjongBehaviorAverageData | undefined
}
interface RowProps {
    data: GameLog,
    behavior: GameBehavior | undefined,
    mahjongBehaviorAverage: MahjongBehaviorAverageData | undefined
}

function Row(props: RowProps) {
    const [open, setOpen] = useState<boolean>(false);
    const {t} = useTranslation();
    const userProfiles = useSelector<AppState, LoadableProfileData>(state => state.userProfiles);
  
    const windChinese = [
      t("mahjong_key.wind_east"), 
      t("mahjong_key.wind_south"), 
      t("mahjong_key.wind_west"), 
      t("mahjong_key.wind_north")
    ];

    const handleRowOpen = () => {
      setOpen(!open);
    }
    const getWinner = (winner: string) => {
      if (winner.startsWith('npc')) {
        return t("common.npcname") + winner.slice(-1);
      } else {
        if (userProfiles.kind === 'loaded') {
          const name = userProfiles.data.find(x => x.user_id === winner);
          return (name !== undefined)? name.name : winner;
        }
      }
    }
  
    return (
      <>
        <TableRow>
          <TableCell style={{ width: 30 }} >
            <IconButton size="small" onClick={() => handleRowOpen()} >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell style={{ width: 90, textAlign: "center" }} >
            <Tooltip title={t("mahjongHistoryTable.tooltip_title_game_no") + props.data.id} placement="top" arrow>
              <div>
                {moment.utc(props.data.game_start_time).local().format('YYYY MMM Do')}
              </div>
            </Tooltip>
          </TableCell>
          <TableCell style={{ width: 90, textAlign: "center" }} >{windChinese[props.data.wind]}{t("mahjong_key.wind")}</TableCell>
          <TableCell style={{ width: 90, textAlign: "center" }} > {props.data.winner === -1 ? t("mahjongHistoryTable.match_result") : getWinner(props.data.players[props.data.winner]?.user_id ?? "unknown")}</TableCell>
          <TableCell style={{ width: 90, textAlign: "center" }} >{props.data.score} {t("mahjong_key.ban")}</TableCell>
        </TableRow >
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto">
              <div style={{ width: "100%" }}>
                <MahjongGameRecord match={props.data} opened={open} />
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    )
  }
  
export default function MahjongHistoryTable(props: MahjongHistoryTableProps) {
  const rowsPerPage = 10;
  const {t} = useTranslation();
  const [page, setPage] = useState<number>(0);
  const [sortedData, setSortedData] = useState<GameLog[]>();
  const handleChangePage = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
    setPage(page);
  }
  useEffect(() => {
    if (props.mahjongData)
      setSortedData(props.mahjongData.sort((a, b) => b.game_start_time - a.game_start_time));
  }, [props.mahjongData]);

  return (
    <Grid item md={12} sm={12} xs={12}>
      <GridCard title={t("mahjongHistoryTable.title_history")} desc={t("mahjongHistoryTable.desc_history")}>
      {
        sortedData?.length !== 0 ? 
        (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow >
                  <TableCell style={{ width: 30 }} />
                  <TableCell style={{ fontSize: 18, textAlign: "center" }}>{t("mahjongHistoryTable.text_date")}</TableCell>
                  <TableCell style={{ fontSize: 18, textAlign: "center" }}>{t("mahjongHistoryTable.text_pos")}</TableCell>
                  <TableCell style={{ fontSize: 18, textAlign: "center" }}>{t("mahjongHistoryTable.text_winner")}</TableCell>
                  <TableCell style={{ fontSize: 18, textAlign: "center" }}>{t("mahjong_key.ban_count")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData !== undefined && sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(x =>
                (
                  <Row
                    data={x}
                    behavior={props.behaviorData.find(y => y.id === x.id)}
                    mahjongBehaviorAverage={props.mahjongBehaviorAverage}
                    key={x.id}
                  />
                ))
                }
              </TableBody>
            </Table>
            <TablePagination 
              component="div" 
              count = {props.mahjongData ? props.mahjongData.length : 0}
              onPageChange = {handleChangePage}
              page = {page}
              rowsPerPage = {rowsPerPage}
              labelRowsPerPage={t("common.rows_per_page")}
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.more_than")}${to}}`}`
              }
            />
          </TableContainer>
        )
        :
        (
          <>
            <h4>{t("no__.data")}</h4>
            <p>{t("common.start_a_game")}</p>
          </>
        )
      }
      </GridCard>
    </Grid>
  )
}